import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { CSSProperties } from 'styled-components'
import { useBoolean } from '../../../hooks'
import { FormField } from '../../form/FormField/FormField'
import { IconEye } from '../../iconography/Icons/Icons'
import { getStepDecimals, numberToStep } from '../NumberInput/NumberInput'
import { InputStyled } from './styles'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const EyeIcon = styled(IconEye)`
  position: absolute;
  right: 8px;
  top: 8px;
  transition: 0.2s opacity ease;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

export type InputProps<Type extends React.InputHTMLAttributes<HTMLInputElement>['type']> = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'step' | 'min' | 'max' | 'type'
> & {
  label?: string
  inputStyles?: CSSProperties
  invalid?: boolean
  errorText?: string
  passwordVisible?: boolean
  onPasswordVisibleChange?: (value: boolean) => void
  required?: boolean
  optional?: boolean
  inputSize?: 'thin' | 'small' | 'medium' | 'big'
  name?: string
  type?: Type
  value?: string | number
} & (Type extends 'number'
    ? {
        /**
         * To enable strict step, you need to pass enableStrictStep
         *
         * */
        step?: number
        min?: number
        max?: number
        onChange?: (
          event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
          value: number | null,
        ) => void
        updateOnInputChange?: boolean
        enableStrictStep?: boolean
        value?: string | number | null
      }
    : {
        onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
        limit?: number
      })

type Ref = React.Ref<HTMLInputElement>

const BaseInputWORef = <Type extends React.InputHTMLAttributes<HTMLInputElement>['type'] = 'text'>(
  { onChange, invalid, ...rest }: InputProps<Type>,
  ref: Ref,
) => {
  // Prevent cursor jumping to the end of line
  const [value, setValue] = useState(rest.value)

  const handleChange = useCallback(
    e => {
      setValue(e.target.value)
      onChange?.(e, e.target.value)
    },
    [onChange],
  )

  useEffect(() => {
    if (rest.value !== value) setValue(rest.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.value])

  return (
    <InputStyled
      ref={ref}
      onChange={e => handleChange(e)}
      data-field-invalid={invalid}
      invalid={invalid}
      {...rest}
      value={value}
    />
  )
}

export const BaseInput = React.forwardRef(BaseInputWORef) as <
  Type extends React.InputHTMLAttributes<HTMLInputElement>['type'] = 'text'
>(
  props: InputProps<Type> & { ref?: Ref },
) => React.ReactElement

const PasswordInput = React.forwardRef<HTMLInputElement, InputProps<'password'>>(
  (
    {
      onChange,
      onFocus = noop,
      onBlur = noop,
      passwordVisible = false,
      onPasswordVisibleChange,
      value,
      type: _type,
      ...rest
    },
    ref,
  ) => {
    const [pwVisible, setVisible] = useBoolean(passwordVisible)
    const [focused, focus, blur] = useBoolean(false)

    const inputType = pwVisible ? 'text' : 'password'
    const showEyeIcon = focused || inputType === 'text'

    useEffect(() => {
      setVisible(passwordVisible)
    }, [passwordVisible, setVisible])

    const handleVisibilityChange = (val: boolean) => {
      if (onPasswordVisibleChange) {
        onPasswordVisibleChange(val)
      } else {
        setVisible(val)
      }
    }

    return (
      <>
        <InputStyled
          ref={ref}
          onFocus={e => {
            focus()
            onFocus(e)
          }}
          onBlur={e => {
            blur()
            onBlur(e)
          }}
          onChange={e => onChange?.(e, e.target.value)}
          type={inputType}
          value={value}
          {...rest}
        />
        {showEyeIcon && (
          <EyeIcon
            onMouseDown={e => {
              e.preventDefault()
              handleVisibilityChange(!pwVisible)
            }}
          />
        )}
      </>
    )
  },
)

const NumberInput = React.forwardRef<HTMLInputElement, InputProps<'number'>>(
  (
    {
      onChange,
      step: stepProp,
      updateOnInputChange: updateOnInputChangeProp,
      value: valueProp,
      onBlur,
      min: minProp,
      max: maxProp,
      ...props
    },
    ref,
  ) => {
    const max = typeof maxProp === 'number' ? maxProp : Infinity
    const step = typeof stepProp === 'number' ? stepProp : 1
    const min = typeof minProp === 'number' ? minProp : step
    const value = typeof valueProp === 'string' ? Number(valueProp) : valueProp
    const updateOnInputChange = updateOnInputChangeProp ? updateOnInputChangeProp : step === 1
    const stepDecimals = useMemo(() => getStepDecimals(step), [step])
    const valueToFixed = (value: number) => (stepDecimals ? value.toFixed(stepDecimals) : value.toString())
    const [inputValue, setInputValue] = useState(
      typeof value === 'number' ? valueToFixed(numberToStep(value, step)) : '',
    )

    const changeValue = (e, newValue: string) => {
      const parsedValue = numberToStep(Math.min(Math.max(Number(newValue), min), max), step)
      setInputValue(valueToFixed(parsedValue))

      if (valueProp !== parsedValue) {
        onChange?.(e, parsedValue)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (updateOnInputChange && onChange) {
        changeValue(e, e.target.value)
      } else {
        setInputValue(e.target.value ? e.target.value : '')
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e)
      if (e.target.value) changeValue(e, e.target.value)
      else {
        setInputValue('')
        onChange?.(e, null)
      }
    }

    useEffect(() => {
      if (Number(valueProp) !== Number(inputValue)) {
        setInputValue(String(valueProp))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueProp])

    return (
      <InputStyled
        ref={ref}
        onChange={handleInputChange}
        onBlur={handleBlur}
        type="number"
        value={inputValue}
        max={max}
        min={min}
        step={step}
        {...props}
      />
    )
  },
)

function isEmptyValue<T = unknown>(value: T) {
  const t = typeof value
  return (t !== 'string' && t !== 'number') || Number.isNaN(value)
}

const InputWORef = <Type extends React.InputHTMLAttributes<HTMLInputElement>['type'] = 'text'>(
  {
    className,
    id,
    type,
    label,
    value = '',
    invalid,
    limit,
    disabled,
    name,
    errorText,
    required,
    optional,
    //@ts-expect-error ts can't find this bcs of the type generic
    enableStrictStep,
    ...rest
  }: InputProps<Type>,
  ref: Ref,
) => {
  let InputComponent: typeof PasswordInput | typeof BaseInput | typeof NumberInput
  switch (type) {
    case 'password':
      InputComponent = PasswordInput
      break
    case 'number':
      InputComponent = enableStrictStep ? NumberInput : BaseInput
      break
    default:
      InputComponent = BaseInput
  }

  return (
    <FormField
      id={id || name}
      name={name}
      className={className}
      value={value || ''}
      limit={limit}
      label={label}
      invalid={invalid}
      errorText={errorText}
      required={required}
      optional={optional}
      render={({ id, name }) => (
        //@ts-expect-error TS can't handle this
        <InputComponent
          id={id}
          ref={ref}
          name={name}
          maxLength={limit}
          invalid={invalid}
          value={type !== 'number' && !enableStrictStep ? (isEmptyValue(value) ? '' : value || '') : value}
          role={'input'}
          disabled={disabled}
          type={type}
          {...rest}
        />
      )}
    />
  )
}

export const Input = React.forwardRef(InputWORef) as <
  Type extends React.InputHTMLAttributes<HTMLInputElement>['type'] = 'text'
>(
  props: InputProps<Type> & { ref?: Ref },
) => React.ReactElement
